// "Something that is X cannot be Y", stated by the ontologist with the reason
// (ruled 2026-10-16, 83.5): QROS assumes no two classes exclude each other, so
// a rule is declared here, as `:X :disjointWith :Y @reason="…"`, and
// Troubleshoot reports any entity that is both and quotes the reason. The rule
// shows among the class's statements below, where it is removed.

import { useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useQueryClient } from '@tanstack/react-query';
import { addStatement } from '@/lib/useEntity';
import { invalidateAfterEdit } from '@/lib/invalidate';
import { labelOf, type SchemaClass } from '@/lib/useOntology';
import { Button } from '@/components/ui/Button';
import { Message } from '@/components/ui/Message';
import { fieldClass } from '@/components/ui/field';

export function DisjointRuleForm({ cls, classes, graph }: { cls: SchemaClass; classes: SchemaClass[]; graph: string }) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [other, setOther] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);
  const [added, setAdded] = useState<string | null>(null);
  const choices = useMemo(
    () => classes.filter((c) => c.uri !== cls.uri).sort((a, b) => labelOf(a).localeCompare(labelOf(b))),
    [classes, cls.uri],
  );
  const name = labelOf(cls);
  const otherName = labelOf(choices.find((c) => c.uri === other) ?? { uri: other, label: null });

  const add = async () => {
    const token = auth.user?.id_token;
    const missing = [!other && 'the class it cannot also be', !reason.trim() && 'the reason'].filter(Boolean);
    if (missing.length > 0) {
      setRefused(`Nothing was added. Add needs ${missing.join(' and ')}.`);
      return;
    }
    if (!token) return;
    setBusy(true);
    setRefused(null);
    const r = await addStatement(
      {
        graph,
        subject: cls.uri,
        predicate: ':disjointWith',
        object: { uri: other },
        tags: [{ name: '@reason', value: { value: reason.trim() } }],
      },
      token,
    );
    setBusy(false);
    if (!r.ok) {
      setRefused(`The rule was not added: ${r.message}`);
      return;
    }
    setAdded(`Added: something that is ${name} cannot be ${otherName}.`);
    setOther('');
    setReason('');
    invalidateAfterEdit(queryClient, graph);
  };

  return (
    <div className="space-y-2">
      <p className="text-xs text-tres-text-secondary">
        Say that something that is {name} cannot also be another class, and why. Troubleshoot reports any entity
        that is both and quotes your reason. Nothing is ruled out until you say so.
      </p>
      <div className="flex flex-wrap items-start gap-2">
        <select
          aria-label={`A class something that is ${name} cannot be`}
          value={other}
          onChange={(e) => {
            setOther(e.target.value);
            setRefused(null);
            setAdded(null);
          }}
          className={fieldClass(refused && !other ? 'error' : 'normal', 'min-w-48 text-sm')}
        >
          <option value="">cannot also be…</option>
          {choices.map((c) => (
            <option key={c.uri} value={c.uri}>
              {labelOf(c)}
            </option>
          ))}
        </select>
        <textarea
          aria-label="Why"
          placeholder="Why: for example, a person is never an organization"
          value={reason}
          rows={2}
          onChange={(e) => {
            setReason(e.target.value);
            setRefused(null);
            setAdded(null);
          }}
          className={fieldClass(refused && !reason.trim() ? 'error' : 'normal', 'min-w-64 flex-1 text-sm')}
        />
        <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => void add()}>
          {busy ? 'Adding…' : 'Add rule'}
        </Button>
      </div>
      {refused && (
        <Message kind="warning" density="compact">
          {refused}
        </Message>
      )}
      {added && (
        <Message kind="outcome" density="compact">
          {added}
        </Message>
      )}
    </div>
  );
}
